/**
 * Documento fiscal: situação, cálculo gravado, pagamentos, eventos, tentativas e ações.
 * `variant="resultado"` (tela final da Venda Direta): a situação, o aviso de ambiente e os
 * arquivos/impressão já estão no cartão "Situação da NF-e" acima; aqui ficam as ações
 * fiscais permitidas (reenvio, abandono, cancelamento, carta de correção) e as abas.
 */
import {
  FISCAL_ENVIRONMENT_LABEL,
  FISCAL_USE_LABEL,
  formatMoney,
  type FiscalDocumentDetail,
  type FiscalPreview,
} from "@cafegestor/shared";
import { useState } from "react";
import { useCan } from "@/app/me";
import { Tabs } from "@/components/patterns";
import { Alert, Badge, Button, Card, Field, Input, Spinner, Table } from "@/components/ui";
import { apiFetch } from "@/lib/api";
import { Checkbox, SaveError } from "../cadastros/widgets";
import { dateTimeBR } from "../estoque/lib";
import { CalcView, TaxBreakdown } from "./CalcView";
import { PrintActions } from "./PrintActions";
import {
  DOC_STATUS_TONE,
  TPAG_LABEL,
  docStatusLabel,
  useFiscalAction,
  useFiscalQuery,
} from "./lib";

const JOB_STATUS_LABEL: Record<string, string> = {
  pendente: "aguardando a vez",
  executando: "em execução",
  concluido: "concluída",
  falhou: "parada (precisa de atenção)",
};

/** Bloco de informação do resumo: o texto inteiro aparece (quebra a linha, nunca corta). */
function InfoBlock({
  label,
  value,
  mono = false,
  strong = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  strong?: boolean;
}) {
  return (
    <div className="min-w-0 rounded-lg border border-border/70 px-4 py-3">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p
        className={`mt-1 break-words ${strong ? "text-lg font-bold tabular-nums" : "text-sm font-medium"} ${
          mono ? "font-mono text-[13px] break-all" : ""
        }`}
      >
        {value}
      </p>
    </div>
  );
}

export function DocStatusBadge({ status }: { status: FiscalDocumentDetail["status"] }) {
  return <Badge tone={DOC_STATUS_TONE[status]}>{docStatusLabel(status)}</Badge>;
}

type Pending = null | "cancelar" | "pedir" | "abandonar" | "cce";

export function DocumentDetail({
  id,
  onClose,
  variant = "completo",
}: {
  id: string;
  onClose?: () => void;
  variant?: "completo" | "resultado";
}) {
  const full = variant === "completo";
  const can = useCan();
  const q = useFiscalQuery<FiscalDocumentDetail>(`/documentos/${id}`);
  const [pending, setPending] = useState<Pending>(null);
  const [text, setText] = useState("");
  const consult = useFiscalAction<Record<string, never>>(() => `/documentos/${id}/consultar`);
  // reenvio: prévia do recálculo → conferir → reenviar com o hash conferido
  const [resendPreview, setResendPreview] = useState<FiscalPreview | null>(null);
  const [resendChecked, setResendChecked] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState<unknown>(null);
  const resend = useFiscalAction<{ previewHash: string }>(
    () => `/documentos/${id}/reenviar`,
    "POST",
    () => {
      setResendPreview(null);
      setResendChecked(false);
    },
  );
  const loadResendPreview = async () => {
    setResendLoading(true);
    setResendError(null);
    setResendChecked(false);
    resend.reset();
    try {
      setResendPreview(await apiFetch<FiscalPreview>(`/fiscal/documentos/${id}/previa-reenvio`));
    } catch (err) {
      setResendError(err);
    } finally {
      setResendLoading(false);
    }
  };
  const act = useFiscalAction<{ reason?: string; correction?: string }>(
    () =>
      pending === "cancelar"
        ? `/documentos/${id}/cancelar`
        : pending === "pedir"
          ? `/documentos/${id}/pedido-cancelamento`
          : pending === "abandonar"
            ? `/documentos/${id}/abandonar`
            : `/documentos/${id}/cce`,
    "POST",
    () => {
      setPending(null);
      setText("");
    },
  );
  if (q.isPending) return <Spinner />;
  if (q.error || !q.data) return <SaveError error={q.error} />;
  const d = q.data;
  const uncertain = ["pendente", "processando", "incerta", "contingencia"].includes(d.status);
  const canRequestCancel =
    d.status === "autorizada" && !d.can.cancel && can("fiscal.cancelamento.solicitar");
  const nfe = d.model === "65" ? "NFC-e" : "NF-e";
  return (
    <Card
      title={full ? `${nfe} ${d.series}-${d.number}` : `Detalhes da ${nfe} ${d.series}-${d.number}`}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <span className="mr-1 text-lg font-bold tabular-nums">{formatMoney(d.totalAmount)}</span>
          <DocStatusBadge status={d.status} />
          {onClose && (
            <Button size="sm" variant="ghost" onClick={onClose}>
              Fechar
            </Button>
          )}
        </div>
      }
    >
      <div className="space-y-4">
        {full && d.environment !== "producao" && (
          <Alert tone="warn">
            {d.environment === "homologacao"
              ? "Documento emitido em ambiente de homologação — sem valor fiscal."
              : `${FISCAL_ENVIRONMENT_LABEL[d.environment]} — documento sem valor fiscal.`}
          </Alert>
        )}
        {full && d.status === "incerta" && (
          <Alert tone="warn">
            Situação incerta: o provedor não respondeu a tempo. O sistema CONSULTA antes de qualquer
            reenvio — nenhuma nota será duplicada.
          </Alert>
        )}
        {full && d.status === "rejeitada" && (
          <Alert tone="error">
            Rejeitada {d.sefazCode ? `(${d.sefazCode})` : ""}: {d.sefazMessage ?? d.statusReason}.
            Corrija a configuração e reenvie (mesmo número), ou abandone e inutilize o número.
          </Alert>
        )}
        <div className="flex flex-wrap gap-2">
          {full && uncertain && (
            <Button
              variant="secondary"
              onClick={() => consult.mutate({})}
              disabled={consult.isPending}
            >
              Consultar agora
            </Button>
          )}
          {d.can.resend && (
            <Button onClick={loadResendPreview} disabled={resendLoading || resend.isPending}>
              {resendLoading ? "Calculando..." : "Recalcular (prévia do reenvio)"}
            </Button>
          )}
          {d.can.abandon && (
            <Button variant="secondary" onClick={() => setPending("abandonar")}>
              Abandonar
            </Button>
          )}
          {d.can.cancel && (
            <Button variant="danger" onClick={() => setPending("cancelar")}>
              Cancelar nota
            </Button>
          )}
          {canRequestCancel && (
            <Button variant="secondary" onClick={() => setPending("pedir")}>
              Pedir cancelamento
            </Button>
          )}
          {d.can.cce && (
            <Button variant="secondary" onClick={() => setPending("cce")}>
              Carta de correção
            </Button>
          )}
        </div>
        <SaveError error={consult.error ?? resendError} />
        {resendPreview && d.can.resend && (
          <div className="space-y-2 rounded-md border p-3">
            <p className="font-semibold">Reenvio com o mesmo número — confira o novo cálculo</p>
            <CalcView calc={resendPreview.calc} />
            {resendPreview.calc.ok ? (
              <>
                <Checkbox
                  label="Conferi destinatário, itens, CFOP, tributos e totais do reenvio."
                  checked={resendChecked}
                  onChange={(e) => setResendChecked(e.target.checked)}
                />
                <Button
                  disabled={!resendChecked || resend.isPending}
                  onClick={() => resend.mutate({ previewHash: resendPreview.hash })}
                >
                  {resend.isPending ? "Enviando..." : "Confirmar reenvio"}
                </Button>
              </>
            ) : (
              <Alert tone="warn">Corrija as pendências acima antes de reenviar.</Alert>
            )}
            <SaveError error={resend.error} />
          </div>
        )}
        {pending && (
          <div className="space-y-2 rounded-md border p-3">
            <Field
              label={
                pending === "cce"
                  ? "Correção (mínimo 15 caracteres)"
                  : "Motivo (mínimo 15 caracteres)"
              }
              hint={
                pending === "cce"
                  ? "A carta de correção não corrige valores, impostos, destinatário nem data."
                  : pending === "abandonar"
                    ? "O número fica reservado e deve ser inutilizado depois."
                    : pending === "cancelar"
                      ? "O cancelamento vai para a SEFAZ e não pode ser desfeito; a venda não é estornada por ele."
                      : "O pedido vai para quem pode cancelar a nota decidir."
              }
            >
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                maxLength={pending === "cce" ? 1000 : 255}
              />
            </Field>
            <div className="flex gap-2">
              <Button
                disabled={text.trim().length < 15 || act.isPending}
                onClick={() =>
                  act.mutate(
                    pending === "cce" ? { correction: text.trim() } : { reason: text.trim() },
                  )
                }
              >
                Confirmar
              </Button>
              <Button variant="ghost" onClick={() => setPending(null)}>
                Voltar
              </Button>
            </div>
            <SaveError error={act.error} />
          </div>
        )}

        {full && <PrintActions doc={d} />}

        <Tabs
          label="Detalhes da nota"
          tabs={[
            {
              key: "resumo",
              label: "Resumo",
              content: (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <InfoBlock label="Ponto de emissão" value={FISCAL_USE_LABEL[d.useKey]} />
                    <InfoBlock label="Operação" value={d.operationName} />
                    <InfoBlock label="Destinatário" value={d.recipientName ?? "—"} />
                    <InfoBlock label="Valor total" value={formatMoney(d.totalAmount)} strong />
                    <InfoBlock label="Protocolo" value={d.protocol ?? "—"} mono />
                    <InfoBlock label="Autorização" value={dateTimeBR(d.authorizedAt)} />
                    {d.cancelDeadline && (
                      <InfoBlock
                        label="Prazo de cancelamento"
                        value={dateTimeBR(d.cancelDeadline)}
                      />
                    )}
                  </div>
                  <dl className="grid gap-x-6 gap-y-2 rounded-lg bg-background/70 px-4 py-3 text-sm sm:grid-cols-2">
                    <div className="min-w-0 sm:col-span-2">
                      <dt className="text-xs font-medium text-muted-foreground">Chave de acesso</dt>
                      <dd className="mt-0.5 font-mono text-[13px] break-all">
                        {d.accessKey ?? "—"}
                      </dd>
                    </div>
                    <div className="min-w-0">
                      <dt className="text-xs font-medium text-muted-foreground">Pedida por</dt>
                      <dd className="mt-0.5 break-words">
                        {d.requestedByEmail ?? "—"} em {dateTimeBR(d.requestedAt)} ({d.source})
                      </dd>
                    </div>
                    {d.job && (
                      <div className="min-w-0">
                        <dt className="text-xs font-medium text-muted-foreground">
                          Fila de emissão
                        </dt>
                        <dd className="mt-0.5 break-words">
                          {JOB_STATUS_LABEL[d.job.status] ?? d.job.status} · {d.job.attempts}{" "}
                          tentativa(s)
                          {d.job.lastError ? ` — ${d.job.lastError}` : ""}
                        </dd>
                      </div>
                    )}
                  </dl>
                  {d.references.length > 0 && (
                    <p className="text-sm">
                      Notas referenciadas:{" "}
                      {d.references.map((r) => `${r.kind} ${r.accessKey}`).join("; ")}
                    </p>
                  )}
                  {d.cancelRequests.length > 0 && (
                    <p className="text-sm">
                      Pedidos de cancelamento:{" "}
                      {d.cancelRequests
                        .map((r) => `${r.status} (${r.requestedByEmail}: ${r.reason})`)
                        .join("; ")}
                    </p>
                  )}
                </div>
              ),
            },
            {
              key: "produtos",
              label: "Produtos",
              badge: d.calc.items.length,
              content: <CalcView calc={{ ...d.calc, issues: [] }} />,
            },
            {
              key: "tributos",
              label: "Tributos",
              content: <TaxBreakdown calc={d.calc} />,
            },
            {
              key: "pagamentos",
              label: "Pagamentos",
              badge: d.payments.length,
              content: (
                <Table
                  head={["#", "Forma", "Valor", "Eletrônico", "Instituição", "Autorização / E2E"]}
                >
                  {d.payments.map((p) => (
                    <tr key={p.line}>
                      <td className="px-3 py-1">{p.line}</td>
                      <td className="px-3 py-1">{TPAG_LABEL[p.tpag] ?? p.tpag}</td>
                      <td className="px-3 py-1 text-right tabular-nums">{formatMoney(p.amount)}</td>
                      <td className="px-3 py-1">{p.electronic ? "sim" : "não"}</td>
                      <td className="px-3 py-1 font-mono">{p.institutionCnpj ?? "—"}</td>
                      <td className="px-3 py-1 font-mono text-xs">
                        {p.authorizationCode ?? p.pixEndToEnd ?? "—"}
                      </td>
                    </tr>
                  ))}
                </Table>
              ),
            },
            {
              key: "historico",
              label: "Histórico e eventos",
              badge: d.events.length + d.attempts.length,
              content: (
                <div className="space-y-5">
                  <div>
                    <h3 className="mb-2 font-semibold">Eventos</h3>
                    {d.events.length === 0 ? (
                      <p className="text-sm text-muted-foreground">
                        Nenhum evento (cancelamento ou carta de correção).
                      </p>
                    ) : (
                      <Table
                        head={[
                          "Evento",
                          "Situação",
                          "Motivo / correção",
                          "Protocolo",
                          "Pedido por",
                        ]}
                      >
                        {d.events.map((e) => (
                          <tr key={e.id}>
                            <td className="px-3 py-1">
                              {e.kind}
                              {e.kind === "cce" ? ` #${e.sequence}` : ""}
                            </td>
                            <td className="px-3 py-1">{e.status}</td>
                            <td className="px-3 py-1">{e.reason ?? e.correction}</td>
                            <td className="px-3 py-1 font-mono">{e.protocol ?? "—"}</td>
                            <td className="px-3 py-1">{e.requestedByEmail}</td>
                          </tr>
                        ))}
                      </Table>
                    )}
                  </div>
                  <div>
                    <h3 className="mb-2 font-semibold">Tentativas junto ao provedor</h3>
                    <Table head={["Quando", "Operação", "Resultado", "SEFAZ", "Mensagem"]}>
                      {d.attempts.map((a, n) => (
                        <tr key={n}>
                          <td className="px-3 py-1 whitespace-nowrap">{dateTimeBR(a.startedAt)}</td>
                          <td className="px-3 py-1">{a.operation}</td>
                          <td className="px-3 py-1">{a.outcome}</td>
                          <td className="px-3 py-1">{a.sefazCode ?? "—"}</td>
                          <td className="px-3 py-1 text-xs">{a.message}</td>
                        </tr>
                      ))}
                    </Table>
                  </div>
                  {d.prints.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Impressões:{" "}
                      {d.prints
                        .map(
                          (p) =>
                            `${dateTimeBR(p.createdAt)} ${p.transport} ${p.result}${p.reprint ? " (reimpressão)" : ""}`,
                        )
                        .join(" · ")}
                    </p>
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>
    </Card>
  );
}
